// ============================================================================
// LE PICKER — choisir UNE valeur dans une liste, au clavier comme à la souris.
//
// ⚠️ SON DESSIN EST CELUI DE LA MAQUETTE, relevé au pixel (2026-08-16) et non composé : déclencheur de
// 36 px à 172 px de large minimum, rayon `--r-ctrl`, fond `--paper` (et non le verre d'une puce de
// filtre), 12,5 px, chevron de 13 px ; panneau à champ de recherche interne sur `--ink-50`, options de
// 32 px, valeur choisie en `teal-100`/`teal-800`/600 — sans coche ni compteur, que la maquette
// n'emploie pas. La version précédente était une invention : elle marchait, elle ne ressemblait à rien.
//
// ⚠️ IL EST LE SECOND CLIENT DE `<Popover>` : une primitive extraite d'un seul appelant n'est pas
// prouvée. Toutes les sorties et la restitution du focus viennent de là.
//
// ⚠️ LA CRÉATION À LA VOLÉE EXISTE DEPUIS LE 2026-08-17, ET ELLE ÉCRIT VRAIMENT (`onCreate`). Ce
// commentaire disait l'inverse — « aucune création : elle suppose une route d'écriture qui n'existe pas
// encore » — et c'est devenu faux le jour où le producteur master-data a su créer une famille. La règle,
// elle, n'a pas bougé : un bouton « Créer » qui n'écrirait rien ferait semblant. D'où le bouton rendu
// SEULEMENT si l'appelant fournit de quoi écrire, et jamais quand la valeur tapée existe déjà.
// ============================================================================
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon } from './icons';
import { checkLabel } from './near-match';
import { FIELD_LABEL } from '@orgazone/ui-tokens';
import { Popover } from './popover';
import { t } from '@orgazone/i18n';

/**
 * Le TON d'un état d'option — un rôle, jamais une couleur choisie par l'appelant.
 * `ok` = abouti · `warn` = en cours · `neutral` = rien encore.
 */
export type PickerTone = 'ok' | 'warn' | 'neutral';

const TONS_ETAT: Record<PickerTone, { dot: string; bg: string; fg: string }> = {
  ok: { dot: 'var(--teal-500)', bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
  warn: { dot: 'var(--amber)', bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' },
  neutral: { dot: 'var(--ink-300)', bg: 'var(--ink-100)', fg: 'var(--ink-600)' },
};

export interface PickerOption {
  value: string;
  label: string;
  /**
   * OÙ EN EST CETTE OPTION — pastille, chiffre, badge. Trois emplacements de PRÉSENTATION, ajoutés
   * 2026-09-03 pour le sélecteur de zone d'inventaire.
   *
   * ⚠️ ILS RESTENT GÉNÉRIQUES, et c'est la condition pour qu'ils vivent ici : le picker ne sait rien
   * d'une zone, d'un comptage ni d'un inventaire — il rend un état que l'appelant lui donne. Y faire
   * entrer le vocabulaire d'un écran obligerait le suivant à y ajouter le sien.
   * ⚠️ CHOISIR UNE OPTION N'EST PAS SON ÉTAT : la surbrillance dit « voilà ce que vous avez pris »,
   * la pastille dit « voilà où en est ce que vous prenez ». Les rendre du même ton les confondrait.
   */
  dot?: PickerTone;
  /** Un chiffre ou une mesure, alignée à droite (« 342 pcs »). */
  hint?: string;
  /** L'état en toutes lettres, en pastille (« Terminée », « En cours »). */
  badge?: { label: string; tone: PickerTone };
}

/**
 * OÙ LE CONTRÔLE EST POSÉ — et ce n'est pas une préférence de style.
 *
 * ⚠️ LA MAQUETTE PORTE UN COMPOSANT ET DEUX HABILLAGES, jamais un recouvrement de taille : 8 emplois
 * du gabarit BARRE D'OUTILS (36 px de haut, 172 px de large au minimum, 12,5 px) et 13 du gabarit
 * CHAMP (40 px, pleine largeur, 13,5 px, intitule au-dessus). Nous n avions que le premier — et
 * c'est lui qui était rendu dans une modale, où il apparaissait comme un bouton isolé de 172 px, sans
 * intitulé, dans une colonne de 436 px.
 *
 * ⚠️ L'ÉCART N'EST PAS QUE COSMÉTIQUE : en barre d'outils, le contrôle FILTRE, donc il offre « toutes
 * les familles » pour révoquer le filtre. Dans un formulaire il pose une VALEUR : offrir une rangée qui
 * remet le choix à vide y ferait « dé-choisir » — la maquette ne le fait jamais.
 */
/**
 * ⚠️ TROIS HABILLAGES, PARCE QUE GÉOMÉTRIE ET SÉMANTIQUE SONT DEUX AXES (corrigé 2026-08-17).
 * `variant` les confondait : « compact » impliquait « filtre », donc une rangée qui RÉVOQUE. Or la
 * maquette pose, dans la barre d'outils des déclinaisons, un contrôle COMPACT qui ÉCRIT (la grille de
 * tailles) — et il s'était retrouvé à offrir « dé-choisir » au milieu d'un formulaire.
 *   · `toolbar`     — compact, FILTRE   : offre la rangée « tout », qui révoque ;
 *   · `field`       — pleine largeur, ÉCRIT : intitulé rendu au-dessus, jamais de « tout » ;
 *   · `field-inline`— compact, ÉCRIT   : même sémantique que `field`, mais l'intitulé est rendu par
 *     l'hôte (la barre écrit déjà « Grille de tailles » à côté ; le répéter ferait doublon).
 *   · `field-bare`  — pleine largeur, ÉCRIT, intitulé rendu par l'hôte : un champ dans une rangée de formulaire
 *     dont l'intitulé est commun à plusieurs contrôles (40 px, comme le champ et le bouton voisins) ;
 *   · `cell`        — remplit sa cellule de TABLEAU, ÉCRIT (34 px) : une valeur choisie ligne par ligne ;
 *   · `cell-head`   — remplit sa cellule d'EN-TÊTE de tableau, ÉCRIT (30 px, 11,5 px) : ce qu'est la colonne. Une
 *     valeur posée s'y lit en sarcelle, le vide en gris — d'un coup d'œil, les colonnes retenues se distinguent.
 * ⚠️ LES TROIS DERNIERS REMPLISSENT LEUR CONTENEUR, jamais plus : posé dans une colonne de 148 px, un déclencheur au
 *    plancher de 220 débordait sous la colonne voisine, et son texte se lisait coupé (import de fichier).
 */
export type PickerVariant = 'toolbar' | 'field' | 'field-inline' | 'field-bare' | 'cell' | 'cell-head';

/** La géométrie du déclencheur, par habillage — relevée sur la maquette (§24.1sexies-bis). */
const TRIGGER_GEOMETRY: Record<PickerVariant, CSSProperties> = {
  toolbar: { height: 36, minWidth: 172, padding: '0 10px', fontSize: '12.5px', gap: 7, display: 'inline-flex' },
  // ⚠️ 220 px et non 172 en `field-inline` : c'est le relevé de la maquette (l.3454), et un contrôle qui porte
  //    « Vêtements FR (XS → XXL) » tronquerait tout à 172.
  'field-inline': { height: 36, minWidth: 220, padding: '0 10px', fontSize: '12.5px', gap: 7, display: 'inline-flex' },
  field: { height: 40, width: '100%', padding: '0 12px', fontSize: '13.5px', gap: 9, display: 'flex' },
  'field-bare': { height: 40, width: '100%', minWidth: 0, padding: '0 10px', fontSize: '12.5px', gap: 7, display: 'flex' },
  cell: { height: 34, width: '100%', minWidth: 0, padding: '0 9px', fontSize: '12.5px', gap: 6, display: 'flex' },
  'cell-head': { height: 30, width: '100%', minWidth: 0, padding: '0 6px', fontSize: '11.5px', fontWeight: 600, gap: 4, display: 'flex' },
};

/** Les encres d'un en-tête de tableau : la valeur posée en sarcelle, le vide en gris (maquette de l'import). */
const HEAD_INKS = {
  set: { border: 'var(--teal-400)', background: 'var(--teal-100)', color: 'var(--teal-800)' },
  empty: { border: 'var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-500)' },
} as const;

interface PickerBase {
  /** Identifiant du panneau — unique dans la page. */
  id: string;
  /** Nom du filtre (« Famille ») : nom accessible du panneau. */
  label: string;
  options: PickerOption[];
  /** Libellé de l'option « tout » — c'est LUI qui s'affiche au repos, comme dans la maquette. */
  allLabel: string;
  /**
   * Habillage. `toolbar` (défaut) = filtre de liste ; `field` = champ de formulaire ou de modale.
   * ⚠️ En `field`, l'intitulé est RENDU au-dessus du contrôle et l'option « tout » n'est plus offerte.
   *
   * ⚠️ NE L’ENVELOPPEZ JAMAIS DANS UN `<label>` — deux pannes, constatées à l’écran le 2026-08-20 :
   *   · l’intitulé apparaît EN DOUBLE, puisque `field` rend déjà le sien ;
   *   · le panneau devient IMPOSSIBLE À FERMER. Un `<button>` est un élément « labelable » : tout clic
   *     dans le label est re-dispatché vers lui, donc le panneau se rouvre à l’instant même où on le
   *     ferme. Aucune erreur, aucun avertissement — juste un panneau qui colle à l’écran.
   * Un `<div>` suffit ; si vous voulez poser l’intitulé vous-même, prenez `field-inline`.
   */
  variant?: PickerVariant;
  /**
   * Déclencheur PLEIN (encre de marque), avec l'intitulé rendu en SURTITRE à l'intérieur.
   *
   * ⚠️ POUR LES CONTRÔLES POSÉS DANS UN BLOC COLORÉ, où un déclencheur blanc se lirait comme un champ
   * de saisie de plus (le sélecteur de zone vit DANS le cadre vert du bloc de scan). Le surtitre y est
   * nécessaire : sans lui, on lit un nom sans savoir de quelle question il est la réponse.
   * ⚠️ N'EST PAS UN ÉTAT : c'est l'emplacement qui le commande, pas ce que le contrôle vaut.
   */
  tone?: 'brand';
  /**
   * Largeur du PANNEAU, en pixels, quand le déclencheur ne la donne pas.
   *
   * ⚠️ PAR DÉFAUT LE PANNEAU FAIT LA LARGEUR DE SON DÉCLENCHEUR, et c'est le bon défaut : sous un champ,
   * un panneau plus étroit ou plus large que ce qu'il complète se lit mal. Mais une rangée qui porte un
   * état (pastille + chiffre + badge) tient plus de place que le nom seul dans le bouton — et les noms
   * s'y tronquaient. On la donne alors explicitement, plutôt que d'élargir le déclencheur, qui n'a
   * aucune raison de grossir.
   */
  panelWidth?: number;
  /**
   * Ce que dit le champ de recherche du panneau.
   *
   * ⚠️ PAR DÉFAUT IL ANNONCE LE NOMBRE de valeurs (« Rechercher parmi 42… ») — utile sur un référentiel,
   * inutile sur une liste courte que l'œil embrasse. L'appelant peut y mettre ce qui aide chez lui.
   * ⚠️ IL NE PROMET QUE CE QUE LE PANNEAU FAIT : ici « Entrée » CHOISIT la ligne sous le curseur, elle
   * ne crée pas — la création est en deux temps, exprès, pour que le doublon soit vu avant d'écrire.
   * Un texte qui annoncerait « Entrée crée » serait une option offerte non implémentée (§23.0).
   */
  searchPlaceholder?: string;
  /**
   * En `field`, offrir une rangée qui DÉTACHE la valeur (« Non communiqué »).
   * ⚠️ NON PAR DÉFAUT : dans un formulaire, « dé-choisir » n'est pas un geste ordinaire — il efface une
   * donnée. L'écran le demande quand le champ est réellement facultatif, il ne l'hérite pas.
   * ⚠️ Distinct de l'option « tout » du filtre : celle-là RÉVOQUE un filtre (rien n'est écrit),
   * celle-ci ÉCRIT un détachement. Même dessin, deux conséquences.
   */
  clearLabel?: string;
  /**
   * CRÉER la valeur tapée, quand elle n'existe pas encore dans le référentiel.
   *
   * ⚠️ ELLE ÉCRIT VRAIMENT. Rendre `true` sans écrire ferait le pire des boutons — celui qui a l'air
   * d'avoir marché. L'appelant crée la ligne dans SA table (le producteur master-data), attend la
   * réponse, et rend la valeur retenue ; le picker la choisit alors et se ferme.
   * ⚠️ Un ÉCHEC de création reste DANS le panneau, avec son motif : la valeur tapée n'est pas perdue,
   * et l'utilisateur voit pourquoi elle n'a pas été créée.
   */
  onCreate?: (valeur: string) => Promise<string>;
  /**
   * Le VERBE du bouton de création (« Créer » par défaut, « Utiliser » quand rien ne s'écrit), suivi de la valeur.
   * ⚠️ Jamais un mot de champ accordé (« Créer la famille ») : le mot vient du magasin, son genre est inconnu.
   */
  createLabel?: string;
  /**
   * Chercher AU-DELÀ des valeurs reçues. Fourni ⇒ la frappe part chercher, et la liste d'ouverture
   * cesse d'être une limite : elle n'est plus qu'un point de départ.
   * ⚠️ SANS LUI, le panneau ne cherche que dans ce qu'il a reçu — sur une liste plafonnée, c'est
   * chercher dans un tiroir dont on a retiré les trois quarts, et conclure que la valeur n'existe pas.
   */
  onSearch?: (terme: string) => Promise<PickerOption[]>;
  /** Inerte tant que les valeurs ne sont pas lues — et il le DIT. */
  loading?: boolean;
  /** Les valeurs n'ont pas pu être lues : le contrôle est inerte, la raison s'affiche à côté. */
  unavailable?: boolean;
  /**
   * POURQUOI le contrôle est inerte, dans SES mots à lui.
   * ⚠️ SANS LUI, LE COMPOSANT INVENTE UN MOTIF : son texte par défaut annonce des valeurs « illisibles »,
   * ce qui est faux dès qu'un contrôle est inerte pour une autre raison — une dimension qui n'a rien à
   * quoi se rattacher, par exemple. Un motif inventé est pire qu'un silence (§23.0).
   */
  unavailableReason?: string;
  /**
   * SOUS CE NOMBRE DE VALEURS, le contrôle rend des PASTILLES au lieu d'un panneau.
   *
   * ⚠️ MULTI-SÉLECTION SEULEMENT, et c'est une présentation, pas un mode : sur trois caisses, un
   * panneau qu'il faut ouvrir cache un choix qu'on aurait vu d'un coup — et sur quinze, les pastilles
   * font passer la barre à la ligne trois fois. `0` (défaut) = toujours le panneau.
   */
  chipsMax?: number;
}

/**
 * LE CONTRÔLE RÉPOND À QUATRE BESOINS, PAR DEUX AXES — jamais par quatre composants.
 *
 * `multiple` (une ligne / des cases) × `onCreate` (fourni ou non) = les quatre combinaisons. La
 * RECHERCHE, elle, est constante : elle est toujours rendue, et c'est un correctif mesuré du
 * 2026-08-16 (sous un seuil, `onSearch` — la recherche SERVEUR — devenait inatteignable).
 *
 * ⚠️ UN SEUL OUTIL PARCE QUE LES MÉCANIQUES SONT LES MÊMES (arbitrage utilisateur 2026-08-20).
 * Filtre et champ partagent recherche, liste, bascule, création et clavier. Ce qui diffère est le
 * SENS DU VIDE — « aucune restriction » d'un côté, « aucune donnée » de l'autre — et le contrôle le
 * porte déjà par deux mots distincts : `allLabel` (filtre) et `clearLabel` (champ).
 *
 * ⚠️ LE CONTRÔLE RAPPORTE, IL NE DÉCIDE PAS. Il se laisse vider même dans un formulaire : l'obligation
 * de non-vide appartient à l'écran, qui connaît sa règle métier. Un select qui refuserait de se vider
 * embarquerait les règles de ses appelants, et il faudrait un jour en écrire un second pour l'écran
 * dont la règle diffère.
 */
export type PickerProps =
  | (PickerBase & {
    multiple?: false;
    /** Valeur choisie, ou `''` pour « toutes ». */
    value: string;
    onChange: (value: string) => void;
  })
  | (PickerBase & {
    multiple: true;
    /** Valeurs retenues. Vide = « toutes » en filtre, « aucune donnée » en champ. */
    value: readonly string[];
    onChange: (value: string[]) => void;
  });

/** Le chevron du déclencheur — 13 px, `--ink-400`, exactement le tracé de la maquette. */
function Chevron() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ink-400)" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

/** La loupe du champ interne — 14 px, à l'encre du conteneur. */
function Loupe() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden>
      <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
    </svg>
  );
}

/**
 * LA TEINTE D'UNE RANGÉE DE LA LISTE — extraite du rendu pour être MESURABLE.
 *
 * ⚠️ TROIS ÉTATS, ET ILS NE DISENT PAS LA MÊME CHOSE :
 *   · CHOISI  → teal de MARQUE. Surtout pas `--ok-*`, qui sont les jetons sémantiques de la réussite :
 *     ils disent « c'est bien », pas « c'est choisi » (l'écart se voyait — une pastille verte au milieu
 *     d'une liste de valeurs) ;
 *   · VISÉ (curseur clavier/souris) → gris neutre. Confondu avec « choisi », on ne sait plus lequel des
 *     deux on lit ;
 *   · ABSENCE (« Non communiqué », « Toutes les familles ») → encre ATTÉNUÉE, TOUJOURS, même quand
 *     c'est la valeur courante. Cette rangée désigne le VIDE : la mettre en surbrillance de sélection
 *     dirait « voilà ce que vous avez choisi » là où la vérité est « vous n'avez rien choisi ».
 *     Décision utilisateur du 2026-08-17, appliquée à TOUS les pickers.
 */
/**
 * CE QUE MONTRE LE DÉCLENCHEUR FERMÉ.
 *
 * ⚠️ LE MOT DU BOUTON DOIT ÊTRE LE MOT DE LA RANGÉE QU'ON VIENT DE CHOISIR (défaut relevé à l'écran le
 * 2026-08-17). On cliquait « Non communiqué » dans la liste, et le contrôle refermé affichait « non
 * qualifié » ou « non renseignée » : deux mots pour un même état, et l'opérateur se demande légitimement
 * s'il a choisi autre chose que ce qu'il croit.
 *
 * ⚠️ LA CAUSE ÉTAIT UN MÉLANGE DE REGISTRES. `allLabel` est le mot du FILTRE (« Toutes les familles » —
 * il révoque), `clearLabel` celui du CHAMP (« Non communiqué » — il détache). Le vide se lisait
 * toujours avec le premier, y compris là où c'est le second qui a été cliqué.
 */
export function pickerTriggerLabel(
  { choisi, clearLabel, allLabel }: { choisi?: string; clearLabel?: string; allLabel: string },
): string {
  if (choisi != null) return choisi;
  // Un champ qui offre le détachement dit SON mot ; un filtre garde le sien.
  return clearLabel ?? allLabel;
}

/**
 * CE QU'ANNONCE LE DÉCLENCHEUR EN MULTI-SÉLECTION.
 *
 * ⚠️ JAMAIS UN COMPTE SEUL (« 3 sélectionnés ») : il faudrait rouvrir le panneau pour savoir LESQUELS,
 * et un filtre qu'on doit ouvrir pour se relire est un filtre invisible (§23.0). On montre la première
 * valeur et on compte le reste — « Rouge +2 » se lit sans rien ouvrir.
 *
 * ⚠️ VIDE ⇒ LE MOT DE L'ÉCRAN, pas « aucune » : `clearLabel` dans un champ (« Non communiqué »),
 * `allLabel` dans un filtre (« Toutes les familles »). Le même vide n'a pas le même sens des deux
 * côtés, et c'est le seul endroit où cette différence se voit.
 */
export function pickerMultiLabel(
  retenues: readonly string[],
  options: readonly PickerOption[],
  { allLabel, clearLabel }: { allLabel: string; clearLabel?: string },
): string {
  if (retenues.length === 0) return clearLabel ?? allLabel;
  const premier = options.find((o) => o.value === retenues[0])?.label ?? (retenues[0] as string);
  return retenues.length === 1 ? premier : `${premier} +${retenues.length - 1}`;
}

export function pickerOptionTone(
  { absence, choisi, vise }: { absence: boolean; choisi: boolean; vise: boolean },
): { background: string; color: string; fontWeight: number } {
  return {
    background: choisi && !absence ? 'var(--teal-100)' : (vise ? 'var(--ink-100)' : 'transparent'),
    color: absence ? 'var(--ink-400)' : (choisi ? 'var(--teal-800)' : 'var(--ink-800)'),
    fontWeight: choisi && !absence ? 600 : 500,
  };
}

/**
 * LE MODE CRÉATION D'UN PICKER — nommer une valeur, avec la garde anti-doublon.
 *
 * ⚠️ TROIS ISSUES POUR TROIS SITUATIONS, et c'est tout l'intérêt (relevé de la maquette) :
 *   · la valeur EXISTE (accents, casse et ordre des mots ignorés) → on ne propose QUE de la choisir.
 *     Créer une seconde « vestes » à côté de « Vestes » est le doublon dans sa forme la plus pure ;
 *   · elle RESSEMBLE à une existante (Dice ≥ 0,72) → on AVERTIT avec le score, et on laisse les DEUX
 *     issues. Bloquer serait faux : « Robe » et « Robes » peuvent être deux familles distinctes. C'est
 *     un doute qu'on porte à l'écran, pas une décision qu'on prend à la place de l'opérateur ;
 *   · elle est NEUVE → création franche.
 *
 * ⚠️ LA COMPARAISON PORTE SUR LES VALEURS RÉELLEMENT OFFERTES. Comparer à une liste tronquée ferait
 * déclarer « neuve » une valeur qui existe au-delà du plafond — c'est ainsi qu'on crée un doublon en
 * croyant bien faire. D'où la recherche serveur, qui remplit cette liste avant d'arriver ici.
 */
function ModeCreation({
  label, valeur, onValeur, existants, lookupFailure, occupe, failure, createLabel, onCancel, onUtiliser, onCreate,
}: {
  label: string;
  valeur: string;
  onValeur: (v: string) => void;
  existants: readonly string[];
  /** La vérification au serveur a échoué : la garde ne voit que la liste reçue — à dire, avec le motif réel. */
  lookupFailure?: string | null;
  occupe: boolean;
  failure: string | null;
  createLabel?: string;
  onCancel: () => void;
  onUtiliser: (libelle: string) => void;
  onCreate: (libelle: string) => void;
}) {
  const verdict = checkLabel(valeur, existants);
  const creable = verdict.etat === 'proche' || verdict.etat === 'neuf';

  return (
    <div style={{ padding: 10, display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          // ⚠️ Le focus ENTRE ici : on vient de demander à nommer quelque chose, le curseur doit y être.
          ref={(el) => { if (el && document.activeElement !== el) el.focus({ preventScroll: true }); }}
          value={valeur}
          onChange={(e) => onValeur(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.preventDefault(); onCancel(); return; }
            if (e.key !== 'Enter') return;
            e.preventDefault();
            // ⚠️ « Entrée » fait CE QUE DIT LE BOUTON PRINCIPAL — jamais autre chose. Sur une valeur
            // existante, c'est « Utiliser » : créer sur Entrée fabriquerait le doublon que tout ce
            // panneau existe pour empêcher.
            if (verdict.etat === 'exact') onUtiliser(verdict.candidat);
            else if (creable && !occupe) onCreate(verdict.valeur);
          }}
          // ⚠️ « DÉSIGNATION », JAMAIS « le/la {champ} » : le mot du champ vient du MAGASIN (« Secteur »,
          //    « Millésime », « Pitouf ») et n'a pas de genre connu — un article posé devant lui donnait
          //    « Nom de la secteur ». Le nom du champ reste dit au lecteur d'écran, sans article.
          placeholder={t('Désignation…')}
          aria-label={t('Désignation — {field}', { field: label })}
          style={{
            flex: 1, minWidth: 0, height: 40, padding: '0 12px', boxSizing: 'border-box',
            border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
            background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-title)', outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={onCancel}
          aria-label={t('Annuler la création')}
          style={{
            width: 40, height: 40, flexShrink: 0, border: '1px solid var(--ink-200)',
            borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-600)',
            cursor: 'pointer', display: 'grid', placeItems: 'center',
          }}
        >
          <Icon name="x" size={15} />
        </button>
      </div>

      {verdict.etat === 'exact' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '10px 12px', borderRadius: 'var(--r-sm)',
          background: 'var(--teal-100)', border: '1px solid var(--teal-300)',
        }}>
          <span style={{ flex: 1, minWidth: 140, fontSize: 'var(--t-body)', color: 'var(--teal-800)' }}>
            {t('« {candidat} » existe déjà.', { candidat: verdict.candidat })}
          </span>
          <button type="button" className="btn-cta" style={{ height: 32 }} onClick={() => onUtiliser(verdict.candidat)}>
            
            {t('Utiliser')}
          </button>
        </div>
      )}

      {verdict.etat === 'proche' && (
        <div style={{
          padding: '10px 12px', borderRadius: 'var(--r-sm)',
          background: 'var(--amber-bg)', border: '1px solid var(--amber)',
        }}>
          <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)' }}>
            
            {t('Ressemble à « {candidat} » — {pourcent} %', { candidat: verdict.candidat, pourcent: verdict.pourcent })}</div>
          <p style={{ margin: '3px 0 9px', fontSize: 'var(--t-meta)', color: 'var(--ink-600)', lineHeight: 1.45 }}>
            
            {t('Deux valeurs proches se confondent à l’usage, et un référentiel pollué ne se nettoie plus.')}
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn-cta" style={{ height: 32 }} onClick={() => onUtiliser(verdict.candidat)}>
              
              {t('Utiliser « {candidat} »', { candidat: verdict.candidat })}</button>
            <button
              type="button"
              className="btn-ghost"
              style={{ height: 32 }}
              aria-disabled={occupe || undefined}
              onClick={() => { if (!occupe) onCreate(verdict.valeur); }}
            >
              {occupe ? t('Création…') : t('Créer quand même')}
            </button>
          </div>
        </div>
      )}

      {verdict.etat === 'neuf' && (
        <button
          type="button"
          className="btn-cta"
          style={{ height: 36, width: '100%' }}
          aria-disabled={occupe || undefined}
          onClick={() => { if (!occupe) onCreate(verdict.valeur); }}
        >
          {occupe ? t('Création…') : `${createLabel ?? t('Créer')} « ${verdict.valeur} »`}
        </button>
      )}

      {lookupFailure && (
        <p role="status" style={{ margin: 0, fontSize: 'var(--t-meta)', color: 'var(--ink-600)', lineHeight: 1.45 }}>
          {/* ⚠️ Il dit ce qu'IL a vérifié, jamais ce que fera le serveur : chaque appelant écrit à sa façon. */}
          {t('Vérification des valeurs existantes indisponible : {motif} — seule la liste affichée a été comparée.', { motif: lookupFailure })}
        </p>
      )}

      {/* ⚠️ L'ÉCHEC RESTE DANS LE PANNEAU, avec son motif réel : la frappe n'est pas perdue, et le
          serveur seul sait pourquoi il a refusé (nom déjà pris, format, droits). */}
      {failure && (
        <p role="alert" style={{ margin: 0, fontSize: 'var(--t-meta)', color: 'var(--danger-ink)', lineHeight: 1.45 }}>
          {failure}
        </p>
      )}
    </div>
  );
}

export function Picker(props: PickerProps) {
  const {
    id, label, options, allLabel, onSearch, loading, unavailable, unavailableReason,
    variant = 'toolbar', clearLabel, onCreate, createLabel, chipsMax = 0, tone, panelWidth, searchPlaceholder,
  } = props;
  const multi = props.multiple === true;
  /** Les valeurs retenues, sous UNE forme — le reste du composant ignore le mode. */
  const retenues: readonly string[] = multi ? props.value : (props.value ? [props.value] : []);
  /**
   * CHOISIR UNE VALEUR — bascule en multi, remplacement en mono.
   * ⚠️ EN MULTI, LE PANNEAU RESTE OUVERT : cocher trois valeurs en rouvrant trois fois n'est pas un
   * geste. En mono il se ferme, parce que le choix est fait.
   * ⚠️ LA VALEUR VIDE VIDE TOUT, dans les deux modes : c'est la ligne « Toutes » (filtre) ou le
   * détachement (champ), et cocher/décocher quinze lignes pour y revenir n'est pas un chemin.
   */
  // ⚠️ MIROIR DES VALEURS VIVES : `retenues` est reconstruit à chaque rendu (l'appelant passe souvent
  //    un tableau neuf), donc l'inscrire en dépendance rendrait `choisir` — et le `useCallback` du
  //    clavier qui en dépend — instable à chaque frappe. La lecture se fait au moment du geste.
  const vif = useRef({ multi, retenues, onChange: props.onChange as (v: never) => void });
  vif.current = { multi, retenues, onChange: props.onChange as (v: never) => void };

  const choisir = useCallback((v: string, close: () => void) => {
    const { multi: m, retenues: r, onChange } = vif.current;
    if (!m) { (onChange as unknown as (x: string) => void)(v); setTerme(''); close(); return; }
    const poser = onChange as unknown as (x: string[]) => void;
    if (v === '') { poser([]); return; }
    poser(r.includes(v) ? r.filter((x) => x !== v) : [...r, v]);
  }, []);
  const [open, setOpen] = useState(false);
  const [terme, setTerme] = useState('');
  const [curseur, setCurseur] = useState(0);
  /** Résultats venus du serveur pour le terme courant. `null` = rien de distant à montrer. */
  const [distants, setDistants] = useState<PickerOption[] | null>(null);
  const [cherche, setCherche] = useState(false);
  const [chercheKo, setChercheKo] = useState<string | null>(null);
  const [cree, setCree] = useState(false);
  const [creeKo, setCreeKo] = useState<string | null>(null);
  /**
   * LE PANNEAU A DEUX MODES (relevé de la maquette, «pkFam.mode«).
   * ⚠️ POURQUOI UN MODE ET PAS UN CHAMP DE PLUS. Le clic sur « Créer » DOIT produire un changement
   * visible : la version précédente se contentait de ramener le curseur au champ de recherche, ce qui,
   * vu de l'écran, ne faisait RIEN — un bouton qui a l'air d'agir et n'agit pas (§23.0). Le mode donne
   * au geste sa contrepartie : un champ dédié, son propre libellé, et une sortie.
   */
  const [enCreation, setEnCreation] = useState(false);
  const [nouveau, setNouveau] = useState('');
  const listeRef = useRef<HTMLDivElement | null>(null);

  // ⚠️ LE CHAMP DE RECHERCHE EST TOUJOURS RENDU (correctif 2026-08-16, mesure). Il était conditionné à
  // un seuil de 8 valeurs « pour ne pas ajouter de bruit » ; la maquette, elle, l'affiche même pour une
  // liste de 4 — et elle a corrigé exactement l'inverse chez elle, au motif que les listes courtes
  // « passaient pour des selects natifs ». Pire : `onSearch` (la recherche SERVEUR) n'est joignable que
  // par ce champ, donc sous le seuil la promesse « une valeur au-delà de la liste reste atteignable en
  // la tapant » devenait fausse — il n'y avait plus rien où taper.

  /**
   * ⚠️ « TOUTES LES … » DISPARAÎT DÈS QU'ON CHERCHE : maintenue en tête, elle rendait l'état « aucun
   * résultat » inatteignable, et Entrée y effaçait le filtre que l'utilisateur cherchait à poser.
   */
  const filtrees = useMemo(() => {
    const t = terme.trim().toLowerCase();
    // ⚠️ En `field`, aucune option « tout » — sauf détachement EXPLICITEMENT demandé par l'écran.
    if (!t) {
      // ⚠️ SEUL LE FILTRE RÉVOQUE. Les deux habillages qui ÉCRIVENT n'offrent jamais cette rangée :
      // elle remettrait le choix à vide sans rien écrire, ce que la maquette ne fait jamais.
      if (variant === 'toolbar') return [{ value: '', label: allLabel }, ...options];
      return clearLabel ? [{ value: '', label: clearLabel }, ...options] : options;
    }
    // ⚠️ Le SERVEUR fait autorité quand il a répondu : il voit le catalogue entier, le filtrage local
    // ne voit que les valeurs déjà reçues. Tant qu'il n'a pas répondu, on montre le filtrage local
    // plutôt que rien — c'est un aperçu, pas une réponse, et l'état de recherche le dit.
    if (distants) return distants;
    return options.filter((o) => o.label.toLowerCase().includes(t));
  }, [options, terme, allLabel, distants, variant, clearLabel]);

  useEffect(() => { setCurseur(0); }, [terme, open]);
  // ⚠️ Un panneau rouvert repart de la LISTE : retrouver le mode création d'il y a dix minutes, sur une
  // frappe qu'on ne se rappelle pas avoir tapée, ferait créer une valeur par inadvertance.
  useEffect(() => { if (!open) { setEnCreation(false); setNouveau(''); setCreeKo(null); } }, [open]);

  /**
   * La frappe part CHERCHER, après un temps de calme.
   * ⚠️ GARDE ANTI-RÉPONSE PÉRIMÉE : deux frappes rapprochées produisent deux requêtes, et rien ne
   * garantit leur ordre d'arrivée — sans ce drapeau, la réponse d'un terme abandonné écrase celle du
   * terme courant, et la liste montre autre chose que ce qui est écrit dans le champ.
   */
  useEffect(() => {
    if (!onSearch || !open) return;
    const t = terme.trim();
    if (!t) { setDistants(null); setChercheKo(null); return; }
    let vivant = true;
    setCherche(true);
    const minuteur = setTimeout(() => {
      onSearch(t)
        .then((r) => { if (vivant) { setDistants(r); setChercheKo(null); } })
        // ⚠️ Un échec ne rend pas une liste vide : elle se lirait « cette valeur n'existe pas ».
        .catch((e: unknown) => { if (vivant) { setDistants(null); setChercheKo(e instanceof Error ? e.message : String(e)); } })
        .finally(() => { if (vivant) setCherche(false); });
    }, 250);
    return () => { vivant = false; clearTimeout(minuteur); };
  }, [terme, open, onSearch]);

  /**
   * LA GARDE ANTI-DOUBLON INTERROGE LE SERVEUR SUR CE QU'ON NOMME — elle ne voit pas que la liste reçue.
   * ⚠️ LA LISTE EST PLAFONNÉE (et certaines dimensions n'y mettent que ce que des articles portent) : comparer à elle
   *    seule déclarait « neuve » une valeur qui existe au-delà, et c'est ainsi qu'on crée un doublon en croyant bien
   *    faire. La frappe du champ de création part donc chercher, comme celle de la recherche.
   * ⚠️ PAR UNE RÉFÉRENCE : l'appelant passe souvent une fonction neuve à chaque rendu, qui relancerait la recherche.
   */
  const searchRef = useRef(onSearch);
  searchRef.current = onSearch;
  const [lookup, setLookup] = useState<PickerOption[]>([]);
  const [lookupKo, setLookupKo] = useState<string | null>(null);
  const canLookup = Boolean(onSearch);
  useEffect(() => {
    const v = nouveau.trim();
    if (!enCreation || !canLookup || !v) { setLookup([]); setLookupKo(null); return; }
    let vivant = true;
    const minuteur = setTimeout(() => {
      searchRef.current?.(v)
        .then((r) => { if (vivant) { setLookup(r); setLookupKo(null); } })
        // ⚠️ Un échec se DIT : la garde ne voit alors que la liste reçue, et l'opérateur doit le savoir.
        .catch((e: unknown) => { if (vivant) { setLookup([]); setLookupKo(e instanceof Error ? e.message : String(e)); } });
    }, 250);
    return () => { vivant = false; clearTimeout(minuteur); };
  }, [enCreation, nouveau, canLookup]);

  /**
   * LES VALEURS VUES VENANT DU SERVEUR — recherche ou garde —, retenues par valeur.
   * ⚠️ SANS ELLES, une valeur choisie au-delà du plafond, ou retenue depuis la garde, n'avait pas de libellé : le
   *    déclencheur retombait sur « À choisir », la valeur introuvable dans `options`.
   */
  const seen = useRef(new Map<string, PickerOption>());
  for (const o of [...(distants ?? []), ...lookup]) if (o.value !== '') seen.current.set(o.value, o);

  /** Ce à quoi la garde compare une valeur qu'on nomme : TOUTE la liste reçue, plus ce que le serveur a renvoyé. */
  const known = useMemo(() => {
    const byLabel = new Map<string, PickerOption>();
    for (const o of [...options, ...(distants ?? []), ...lookup]) if (o.value !== '' && !byLabel.has(o.label)) byLabel.set(o.label, o);
    return [...byLabel.values()];
  }, [options, distants, lookup]);

  // Le curseur se ramène dans la vue : sorti du cadre, il donne l'impression que les flèches ne
  // répondent plus.
  useEffect(() => {
    if (!open) return;
    listeRef.current?.querySelector<HTMLElement>(`[data-i="${curseur}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [curseur, open]);

  const choisi = multi ? undefined : options.find((o) => o.value === retenues[0]) ?? (retenues[0] ? seen.current.get(retenues[0]) : undefined);
  const inerte = Boolean(loading || unavailable);

  const geom = useMemo(
    // ⚠️ Mémoïsée : recréée à chaque rendu, elle relançait la mesure d'ancrage, qui pose un état.
    // ⚠️ AUCUN PLAFOND DE LARGEUR : la maquette fait le panneau EXACTEMENT aussi large que son
    // déclencheur. Le plafond de 320 px ne mordait pas en barre d'outils (déclencheur ~172 px) mais
    // rétrécissait le panneau de 116 px sous un champ de modale étiré à 436. `undefined` est nécessaire
    // — omettre la clé laisserait le défaut de `POPOVER_GEOM` (420) reprendre la main à la fusion.
    () => ({ width: panelWidth ?? ('trigger' as const), maxWidth: undefined, minHeight: 160, maxHeight: 360, align: 'start' as const }),
    [panelWidth],
  );

  /**
   * ⚠️ POSÉ SUR LE PANNEAU par `<Popover>`, jamais sur son contenu : un événement clavier remonte de
   * l'élément focalisé, il ne descend pas. Sur un enfant, flèches et Entrée n'étaient jamais entendues.
   */
  const auClavier = useCallback((e: React.KeyboardEvent, close: () => void) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const pas = e.key === 'ArrowDown' ? 1 : -1;
      setCurseur((c) => (filtrees.length ? (c + pas + filtrees.length) % filtrees.length : 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const target = filtrees[curseur];
      // Aucune ligne sous le curseur : on ne fait RIEN. Retomber sur la première effacerait le filtre.
      if (!target) return;
      // ⚠️ MÊME CHEMIN QUE LE CLIC : deux recettes de sélection finiraient par diverger, et c'est le
      //    clavier qui perdrait — personne ne le teste à la main.
      choisir(target.value, close);
    }
  }, [filtrees, curseur, choisir]);

  // ⚠️ SOUS LE SEUIL, DES PASTILLES ET PAS DE PANNEAU — même contrôle, autre présentation. Sur trois
  //    valeurs, un panneau qu'il faut ouvrir cache un choix qu'on aurait vu d'un coup ; au-delà, les
  //    pastilles font passer la barre à la ligne et poussent le contenu hors de l'écran.
  // ⚠️ AUCUNE RECHERCHE ICI, ET C'EST COHÉRENT : chercher parmi trois valeurs toutes affichées n'a pas
  //    d'objet. La recherche SERVEUR (`onSearch`), elle, n'a pas de sens non plus sur un ensemble clos
  //    assez petit pour tenir en pastilles.
  if (multi && chipsMax > 0 && options.length <= chipsMax && !onCreate) {
    return (
      <div role="group" aria-label={label} style={{ display: 'inline-flex', gap: 5, flexWrap: 'wrap' }}>
        <PastilleChoix active={retenues.length === 0} onClick={() => choisir('', () => {})} disabled={inerte}>
          {clearLabel ?? allLabel}
        </PastilleChoix>
        {options.filter((o) => o.value !== '').map((o) => (
          <PastilleChoix
            key={o.value}
            active={retenues.includes(o.value)}
            onClick={() => choisir(o.value, () => {})}
            disabled={inerte}
          >
            {o.label}
          </PastilleChoix>
        ))}
      </div>
    );
  }

  return (
    <Popover
      id={id}
      label={t('{field} — choisir une valeur', { field: label })}
      role="dialog"
      open={open}
      onOpenChange={(o) => { setOpen(o); if (!o) setTerme(''); }}
      focus="keep"
      geom={geom}
      onPanelKeyDown={auClavier}
      // ⚠️ COLONNE FLEX : sans elle, `flexShrink:0` sur le champ et `flex:1` sur la liste ne s'appliquent
      // à rien — et dès que l'ancrage rabote la hauteur (déclencheur en bas d'écran), c'est le PANNEAU
      // ENTIER qui défile : le champ de recherche et le message d'échec sortent du cadre.
      style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      trigger={(api) => {
        const head = variant === 'cell-head' ? HEAD_INKS[retenues.length ? 'set' : 'empty'] : null;
        const bouton = (
        <button
          ref={api.ref}
          type="button"
          className="pk-trigger"
          aria-expanded={api['aria-expanded']}
          aria-haspopup={api['aria-haspopup']}
          aria-controls={api['aria-controls']}
          // ⚠️ `aria-disabled` et NON `disabled` : un contrôle désactivé ne reçoit ni focus ni survol,
          // donc son infobulle ne s'affiche JAMAIS — le motif du refus devenait inatteignable, y compris
          // au clavier. Ici il reste focalisable et dit pourquoi il ne s'ouvre pas.
          aria-disabled={inerte || undefined}
          onClick={() => { if (!inerte) api.onClick(); }}
          title={unavailable ? (unavailableReason ?? t('Valeurs de {field} illisibles — le filtre est indisponible.', { field: label })) : undefined}
          style={{
            ...TRIGGER_GEOMETRY[variant],
            border: `1px solid ${tone === 'brand' ? 'var(--teal-800)' : head ? head.border : 'var(--ink-200)'}`,
            borderRadius: variant === 'cell-head' ? 'var(--r-xs)' : 'var(--r-ctrl)',
            background: tone === 'brand' ? 'var(--teal-800)' : head ? head.background : 'var(--paper)',
            color: tone === 'brand' ? '#fff' : head ? head.color : undefined,
            fontWeight: tone === 'brand' ? 700 : TRIGGER_GEOMETRY[variant].fontWeight,
            alignItems: 'center', textAlign: 'left', boxSizing: 'border-box',
            cursor: inerte ? 'not-allowed' : 'pointer',
          }}
        >
          {tone === 'brand' ? (
            <span aria-hidden style={{ fontSize: '9.5px', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', opacity: 0.72, flexShrink: 0 }}>
              {label}
            </span>
          ) : null}
          {/* ⚠️ LE CHIFFRE DE L'OPTION RETENUE, sur le déclencheur : sur un contrôle qu'on repose entre
              deux bips, « où j'en suis ici » doit se lire SANS rouvrir le panneau. Rendu après le nom
              pour que ce soit le nom qu'on lise d'abord. */}
          {tone === 'brand' && choisi?.hint ? (
            <span style={{ fontSize: 'var(--t-meta)', fontWeight: 700, opacity: 0.72, fontVariantNumeric: 'tabular-nums', flexShrink: 0, order: 2 }}>
              {choisi.hint}
            </span>
          ) : null}
          {/* ⚠️ Au repos, c'est « Toutes les familles » qui s'affiche, PAS le nom du filtre : la maquette
              n'écrit jamais « Famille » dans le bouton — le libellé de l'option « tout » le porte déjà. */}
          {/* ⚠️ EN HABILLAGE « BRAND », LE NOM HÉRITE — il ne repeint pas (corrigé 2026-09-04, vu à
              l'écran). L'encre `--ink-900` est juste sur le déclencheur BLANC, et illisible sur le fond
              vert sombre : le nom y apparaissait gris-noir, plus effacé que son propre surtitre. Une
              couleur écrite pour un fond ne survit pas au changement de fond. */}
          <span style={{
            flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            // ⚠️ EN EN-TÊTE, LE NOM HÉRITE de l'encre de l'état ; EN CELLULE, il garde l'encre d'un champ (la maquette ne
            //    grise pas « Ne pas classer » : c'est un choix comme un autre, pas une absence de choix).
            color: tone === 'brand' || head ? undefined : variant === 'cell' ? 'var(--ink-900)' : retenues.length ? 'var(--ink-900)' : 'var(--ink-600)',
            opacity: tone === 'brand' && !retenues.length ? 0.72 : undefined,
            fontWeight: head || variant === 'cell' ? undefined : retenues.length ? 600 : undefined,
          }}>
            {loading
              ? t('Chargement…')
              : multi
                ? pickerMultiLabel(retenues, options, { allLabel, clearLabel })
                : pickerTriggerLabel({ choisi: choisi?.label, clearLabel, allLabel })}
          </span>
          <span style={{ display: 'inline-flex', order: 3, flexShrink: 0 }}><Chevron /></span>
        </button>
        );
        if (variant !== 'field') return bouton;
        // ⚠️ L'INTITULÉ EST RENDU EN `field`. La prop `label` ne servait qu'au nom accessible du panneau :
        // dans une modale, l'utilisateur voyait un bouton isolé portant « Choisir une famille… » sans
        // savoir de quel champ il s'agissait. Les quatre valeurs sont celles de `FIELD_LABEL.form` —
        // exactement celles de la maquette, déjà écrites dans la charte.
        return (
          <div>
            <span style={{
              display: 'block', fontSize: FIELD_LABEL.form.fontSize, fontWeight: FIELD_LABEL.form.weight,
              color: `var(--${FIELD_LABEL.form.ink})`, marginBottom: FIELD_LABEL.form.marginBottom,
            }}>{label}</span>
            {bouton}
          </div>
        );
      }}
    >
      {({ close }) => (enCreation && onCreate ? (
        // ⚠️ LE MODE CRÉATION REMPLACE LE PANNEAU, il ne s'ajoute pas dessous : on n'est plus en train de
        // CHOISIR, on est en train de NOMMER. Garder la liste derrière inviterait à cliquer une valeur
        // pendant qu'on en tape une autre, et personne ne saurait laquelle part.
        <ModeCreation
          label={label}
          valeur={nouveau}
          onValeur={setNouveau}
          existants={known.map((o) => o.label)}
          lookupFailure={lookupKo}
          occupe={cree}
          failure={creeKo}
          createLabel={createLabel}
          onCancel={() => { setEnCreation(false); setCreeKo(null); }}
          onUtiliser={(libelle) => {
            const trouve = known.find((o) => o.label === libelle);
            if (trouve) { choisir(trouve.value, close); setTerme(''); setEnCreation(false); }
          }}
          onCreate={(libelle) => {
            setCree(true);
            setCreeKo(null);
            onCreate(libelle)
              .then((retenue) => {
                // ⚠️ ON POSE CE QUE LE SERVEUR A RETENU, pas ce qui a été tapé : le référentiel normalise
                // (espaces, casse), et afficher la frappe ferait diverger l'écran de la base.
                // ⚠️ EN MULTI, LA VALEUR CRÉÉE REJOINT la sélection au lieu de la remplacer : on vient
                //    de l'inventer pour l'ajouter, pas pour effacer ce qui était coché.
                choisir(retenue, close);
                setTerme('');
                setEnCreation(false);
              })
              .catch((e) => setCreeKo(e instanceof Error ? e.message : String(e)))
              .finally(() => setCree(false));
          }}
        />
      ) : (
        <>
          <div style={{ padding: 8, borderBottom: '1px solid var(--ink-100)', flexShrink: 0 }}>
              {/* Le champ interne de la maquette : pas une bordure, une BOÎTE teintée qui porte la loupe. */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, height: 32, padding: '0 10px',
                borderRadius: 'var(--r-xs)', background: 'var(--ink-50)', color: 'var(--ink-400)',
              }}>
                <Loupe />
                <input
                  // ⚠️ `ref` de rappel : le panneau n'existe qu'après la mesure d'ancrage, donc un effet
                  // lancé à l'ouverture ne trouvait aucun champ — la première ouverture partait sans focus.
                  // ⚠️ `preventScroll` (tactile, 2026-08-25) : sans lui le navigateur mobile fait défiler
                  // la page pour amener le champ au-dessus du clavier — et ce défilement, dont la cible
                  // est le document, fermait le panneau qu'on venait d'ouvrir. Vu de l'écran : la liste
                  // s'ouvre, la page saute en haut, la liste disparaît.
                  ref={(el) => { if (el && document.activeElement !== el) el.focus({ preventScroll: true }); }}
                  // ⚠️ PAS `type="search"` : le navigateur y ajoute SA croix d'effacement et sa propre
                  // métrique, deux éléments que la maquette ne dessine pas — dans une capsule de 32 px
                  // déjà occupée par la loupe. Même arbitrage que le champ de recherche de l'écran.
                  type="text"
                  role="combobox"
                  aria-expanded
                  aria-controls={`${id}-liste`}
                  aria-activedescendant={filtrees[curseur] ? `${id}-opt-${curseur}` : undefined}
                  id={`${id}-q`}
                  aria-label={t('Filtrer les valeurs de {field}', { field: label })}
                  value={terme}
                  onChange={(e) => setTerme(e.target.value)}
                  placeholder={searchPlaceholder ?? (onSearch ? t('Rechercher dans le catalogue…') : t('Rechercher parmi {n}…', { n: options.length }))}
                  style={{
                    flex: 1, minWidth: 0, border: 'none', background: 'transparent', outline: 'none',
                    fontSize: 'var(--t-lead)', color: 'var(--ink-900)',
                  }}
                />
            </div>
          </div>

          <div ref={listeRef} id={`${id}-liste`} role="listbox" aria-label={label} aria-multiselectable={multi || undefined}
            // ⚠️ `overscroll-behavior: contain` (tactile 2026-08-24) : en butée, le geste du doigt ne
            // chaîne pas au défileur parent — un scroll extérieur fermerait le panneau.
            style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: 5, overscrollBehavior: 'contain' }}>
            {filtrees.length === 0 && (
              <div style={{ padding: '14px 10px', fontSize: '12.5px', color: 'var(--ink-400)', textAlign: 'center' }}>
                {cherche ? t('Recherche…') : t('Aucun résultat')}
              </div>
            )}
            {filtrees.map((o, i) => {
              const on = o.value === '' ? retenues.length === 0 : retenues.includes(o.value);
              // ⚠️ LA RANGÉE D'ABSENCE NE SE PEINT JAMAIS COMME UN CHOIX (décision utilisateur
              // 2026-08-17, appliquée PARTOUT). « Non communiqué » / « Toutes les familles » désignent
              // le VIDE : les mettre en surbrillance de sélection dirait « voilà ce que vous avez
              // choisi » là où la vérité est « vous n'avez rien choisi ». Elle reste donc en encre
              // atténuée, y compris quand elle est la valeur courante — ce qui la fait aussi lire comme
              // ce qu'elle est : le moyen de REVENIR au vide.
              const absence = o.value === '';
              return (
                <button
                  key={o.value || '__all__'}
                  id={`${id}-opt-${i}`}
                  data-i={i}
                  type="button"
                  role="option"
                  aria-selected={on}
                  onMouseEnter={() => setCurseur(i)}
                  // ⚠️ `close` vient du Popover : fermer par un état local sauterait la restitution du focus.
                  onClick={() => choisir(o.value, close)}
                  style={{
                    display: multi || o.dot || o.hint || o.badge ? 'flex' : 'block', alignItems: 'center', gap: 9,
                    width: '100%', height: o.badge || o.hint ? 34 : 32, padding: '0 10px', border: 'none',
                    borderRadius: 'var(--r-xs)',
                    // ⚠️ La décision vit dans `pickerOptionTone` — testable sans DOM (convention du paquet).
                    ...pickerOptionTone({ absence, choisi: on, vise: i === curseur }),
                    fontSize: 'var(--t-lead)',
                    textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    cursor: 'pointer',
                  }}
                >
                  {/* ⚠️ UNE CASE, PAS SEULEMENT UNE TEINTE : en multi, la seule surbrillance ne dit pas
                      qu'on peut en cocher plusieurs — l'affordance doit précéder l'essai.
                      ⚠️ La ligne « tout » n'en porte pas : elle RÉVOQUE, elle ne s'ajoute pas. */}
                  {multi && !absence && (
                    <span aria-hidden style={{
                      width: 16, height: 16, flexShrink: 0, borderRadius: 'var(--r-xs)',
                      border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-300)'}`,
                      background: on ? 'var(--teal-500)' : 'var(--paper)',
                      color: 'var(--cta-ink)', display: 'grid', placeItems: 'center',
                      fontSize: '11px', lineHeight: 1,
                    }}>{on ? '✓' : ''}</span>
                  )}
                  {/* ⚠️ LA PASTILLE D'ÉTAT PRÉCÈDE LE NOM : elle se lit en balayant la colonne de gauche,
                      sans avoir à parcourir chaque ligne jusqu'à son bout. */}
                  {o.dot ? (
                    <span aria-hidden style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: TONS_ETAT[o.dot].dot, flexShrink: 0 }} />
                  ) : null}
                  <span style={{
                    minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                    ...(o.hint || o.badge ? { flex: 1 } : null),
                  }}>{o.label}</span>
                  {o.hint ? (
                    <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flexShrink: 0 }}>{o.hint}</span>
                  ) : null}
                  {o.badge ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 'var(--r-xs)',
                      background: TONS_ETAT[o.badge.tone].bg, color: TONS_ETAT[o.badge.tone].fg,
                      fontSize: 'var(--t-eyebrow)', fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase',
                      whiteSpace: 'nowrap', flexShrink: 0,
                    }}>{o.badge.label}</span>
                  ) : null}
                </button>
              );
            })}
          </div>

          {/* ⚠️ PIED PERMANENT (corrigé le 2026-08-17, relevé l.2917-2921). Je l'avais rendu CONDITIONNEL
              — visible seulement après avoir tapé une valeur inconnue — en me justifiant par le risque
              de doublon. C'était une rationalisation d'un choix que je n'avais pas vérifié : la maquette
              pose ce bouton dans un pied FIXE du panneau (filet de séparation, fond «--ink-50«), et
              traite le doublon autrement — et mieux : quand la frappe correspond EXACTEMENT à une valeur
              existante, elle propose « Utiliser » au lieu de « Créer ».
              ⚠️ Une commande qu'il faut deviner en tapant n'existe pas pour qui ne devine pas. */}
          {onCreate && (() => {
            const dejaLa = filtrees.find((o) => o.value !== '' && o.label.toLowerCase() === terme.trim().toLowerCase());
            return (
            <div style={{
              borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)', padding: 8, flexShrink: 0,
            }}>
              <button
                type="button"
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%', height: 36, padding: '0 10px',
                  border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent',
                  color: 'var(--teal-700)', fontSize: 'var(--t-lead)', fontWeight: 600, textAlign: 'left', cursor: 'pointer',
                }}
                aria-disabled={cree || undefined}
                onClick={() => {
                  if (cree) return;
                  // ⚠️ LE PIED OUVRE LE MODE, il ne crée pas. La frappe en cours devient le point de
                  // départ : on ne la fait pas retaper, et c'est elle qui déclenche la garde anti-doublon.
                  setNouveau(terme.trim());
                  setCreeKo(null);
                  setEnCreation(true);
                }}
              >
                {/* ⚠️ TROIS LIBELLÉS POUR TROIS ACTIONS RÉELLEMENT DIFFÉRENTES. « Créer » sur une
                    valeur qui existe déjà mentirait sur ce que le clic va faire, et « Créer « » » sur
                    un champ vide promettrait une ligne sans nom. */}
                <Icon name={dejaLa ? 'check' : 'plus'} size={15} />
                {cree
                  ? t('Création…')
                  : !terme.trim()
                    ? `${createLabel ?? t('Créer')}…`
                    : dejaLa
                      ? t('Utiliser « {value} »', { value: dejaLa.label })
                      : t('{action} « {value} »', { action: createLabel ?? t('Créer'), value: terme.trim() })}
              </button>
              {/* ⚠️ L'ÉCHEC RESTE ICI : la valeur tapée n'est pas perdue, et le motif est le vrai. */}
              {creeKo && (
                <p role="alert" style={{ margin: '6px 2px 0', fontSize: 'var(--t-meta)', color: 'var(--danger-ink)', lineHeight: 1.45 }}>
                  {creeKo}
                </p>
              )}
            </div>
            );
          })()}

          {/* ⚠️ PLUS DE NOTE DE TRONCATURE : elle disait qu'il manquait des valeurs sans donner le moyen
              de les atteindre — un cul-de-sac. La recherche va désormais au serveur, donc tout est
              atteignable et il n'y a plus rien à annoncer. Ce qui se dit encore, c'est l'ÉCHEC : sans
              lui, une recherche en panne rendrait une liste vide qui se lit « ça n'existe pas ». */}
          {chercheKo && (
            <p role="alert" style={{ margin: 0, padding: '8px 10px', borderTop: '1px solid var(--ink-100)', fontSize: 'var(--t-meta)', color: 'var(--danger-ink)', lineHeight: 1.45 }}>
              
              {t('La recherche n’a pas abouti : {chercheKo}', { chercheKo })}
            </p>
          )}
        </>
      ))}
    </Popover>
  );
}

/**
 * UNE PASTILLE DE CHOIX — la présentation du contrôle sous le seuil `chipsMax`.
 * ⚠️ GÉOMÉTRIE DE LA MAQUETTE (h40, trait 1,5 px, `r-sm`) : elle voisine des champs et des listes dans
 * une même ligne, et une hauteur différente y ferait lire deux familles de contrôles.
 */
function PastilleChoix({ active, onClick, disabled, children }: {
  active: boolean; onClick: () => void; disabled?: boolean; children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 40, padding: '0 13px', borderRadius: 'var(--r-sm)',
        border: `1.5px solid ${active ? 'var(--teal-400)' : 'var(--ink-200)'}`,
        background: active ? 'var(--teal-100)' : 'var(--ink-50)',
        color: active ? 'var(--teal-800)' : 'var(--ink-700)',
        fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}
